import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { Brand } from '../types'

// Logo search server URL (standalone server)
const LOGO_SEARCH_SERVER = import.meta.env.VITE_LOGO_SEARCH_SERVER || 'http://localhost:3000'

export interface LogoSearchResult {
  url: string
  thumbnailUrl: string
  title: string
  domain: string
  confidence: number
  confidenceLevel: 'high' | 'medium' | 'low'
}

export interface LogoSearchUsage {
  searchesToday: number
  maxSearches: number
  remaining: number
  resetTime: string
}

export function useBrandsWithoutLogos(client: SupabaseClient | null) {
  const [brands, setBrands] = useState<Brand[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBrands = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: queryError } = await client
        .from('brands')
        .select('*')
        .is('logo_url', null)
        .in('ownership_type', ['family_owned', 'founder_owned', 'employee_owned', 'cooperative'])
        .order('brand_name', { ascending: true })
        .limit(100)

      if (queryError) throw queryError
      setBrands(data || [])
    } catch (err: any) {
      console.error('Error fetching brands without logos:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  return { brands, isLoading, error, refetch: fetchBrands }
}

export function useLogoSearch() {
  const [results, setResults] = useState<LogoSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usage, setUsage] = useState<LogoSearchUsage | null>(null)

  const searchLogo = useCallback(async (brandName: string) => {
    setIsSearching(true)
    setError(null)
    setResults([])

    try {
      const response = await fetch(`${LOGO_SEARCH_SERVER}/api/search-logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandName }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Search failed')
      }

      const data = await response.json()
      setResults(data.results || [])
      if (data.usage) {
        setUsage(data.usage)
      }
    } catch (err: any) {
      console.error('Logo search error:', err)
      setError(err.message)
    } finally {
      setIsSearching(false)
    }
  }, [])

  const fetchUsage = useCallback(async () => {
    try {
      const response = await fetch(`${LOGO_SEARCH_SERVER}/api/usage`)
      if (response.ok) {
        const data = await response.json()
        setUsage(data.usage)
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err)
    }
  }, [])

  return { results, isSearching, error, usage, searchLogo, fetchUsage, clearResults: () => setResults([]) }
}

export function useLogoSave(client: SupabaseClient | null) {
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const saveLogo = useCallback(async (brandId: string, logoUrl: string) => {
    if (!client) return false

    setIsSaving(true)
    setError(null)

    try {
      const { error: updateError } = await client
        .from('brands')
        .update({ logo_url: logoUrl, updated_at: new Date().toISOString() })
        .eq('id', brandId)

      if (updateError) throw updateError
      return true
    } catch (err: any) {
      console.error('Error saving logo:', err)
      setError(err.message)
      return false
    } finally {
      setIsSaving(false)
    }
  }, [client])

  return { saveLogo, isSaving, error }
}

export function useBatchLogoProcess() {
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, processed: [] as string[] })
  const [error, setError] = useState<string | null>(null)

  const startBatchProcess = useCallback(async (brandIds: string[]) => {
    setIsProcessing(true)
    setError(null)
    setProgress({ current: 0, total: brandIds.length, processed: [] })

    try {
      const response = await fetch(`${LOGO_SEARCH_SERVER}/api/batch-process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandIds }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Batch process failed')
      }

      const data = await response.json()
      setProgress({
        current: data.processed || 0,
        total: brandIds.length,
        processed: data.results?.map((r: any) => r.brandId) || [],
      })
      return data
    } catch (err: any) {
      console.error('Batch process error:', err)
      setError(err.message)
      return null
    } finally {
      setIsProcessing(false)
    }
  }, [])

  return { startBatchProcess, isProcessing, progress, error }
}
